import { YesNoEnum } from '@/models/app';
import {
  RideServiceType,
  type DeliveryEstimatePayload,
  type PaidFor,
  type PharmacyDeliveryInfo,
  type SpecialRequestDeliveryInfo,
} from '@/models/ride';
import { dobStringToMonthDayYear } from '@/Utils/callerWithDobParts';

const dollarsToCents = (dollars: number | null | undefined): number | null => {
  if (dollars === null || dollars === undefined || Number.isNaN(Number(dollars))) {
    return null;
  }

  return Math.round(Number(dollars) * 100);
};

export const toBirthdayDate = (value: string | null | undefined): Date | null => {
  const parts = dobStringToMonthDayYear(value);
  return parts ? new Date(parts.dobYear, parts.dobMonth - 1, parts.dobDay) : null;
};

const mapPaidForToBackend = (paidFor: PaidFor | null): PaidFor | null => {
  if (paidFor === 'Yes' || paidFor === 'No' || paidFor === 'noPaymentRequired') {
    return paidFor;
  }

  return null;
};

type SharedDeliveryInput =
  | { deliveryType: RideServiceType.SpecialRequest; info: SpecialRequestDeliveryInfo }
  | { deliveryType: RideServiceType.PharmacyDelivery; info: PharmacyDeliveryInfo };

export const buildSharedDeliveryEstimatePayload = ({ info, deliveryType }: SharedDeliveryInput): DeliveryEstimatePayload | null => {
  const paidFor = mapPaidForToBackend(info.paidFor);
  const isSpecialRequest = deliveryType === RideServiceType.SpecialRequest;
  const valueCents = isSpecialRequest ? dollarsToCents(info.orderValueDollars) : 0;
  const tipAmountCents = dollarsToCents(info.driverTipDollars);

  if (!paidFor || !info.itemCount) {
    return null;
  }

  const payload: DeliveryEstimatePayload = {
    storeName: info.storeName.trim(),
    isStoreOpen: info.storeOpen === YesNoEnum.Yes,
    paidFor,
    itemCount: info.itemCount,
    valueCents,
    ...(info.deliveryOptions ? { [info.deliveryOptions]: true } : {}),
  };

  if (info.pickupInstructions.trim()) {
    payload.pickupInstructions = info.pickupInstructions.trim();
  }
  if (info.deliveryInstructions.trim()) {
    payload.deliveryInstructions = info.deliveryInstructions.trim();
  }
  if (tipAmountCents !== null && tipAmountCents > 0) {
    payload.tipAmountCents = tipAmountCents;
  }
  if (info.storePhoneNumber.trim()) {
    payload.pickupPhoneNumber = info.storePhoneNumber.trim();
  }

  return payload;
};

export const createEmptyPharmacyDeliveryInfo = (defaults?: { patientName?: string; birthday?: string }): PharmacyDeliveryInfo => ({
  storeName: '',
  storePhoneNumber: '',
  storeOpen: '',
  medicationName: '',
  controlledSubstance: '',
  paidFor: null,
  itemCount: null,
  patientName: defaults?.patientName || '',
  birthday: toBirthdayDate(defaults?.birthday),
  pickupInstructions: '',
  deliveryOptions: '',
  deliveryInstructions: '',
  driverTipDollars: null,
  notesForLP: '',
});

export const createEmptySpecialRequestDeliveryInfo = (defaults?: { recipientName?: string }): SpecialRequestDeliveryInfo => ({
  storeName: '',
  storePhoneNumber: '',
  storeOpen: '',
  itemType: '',
  itemDescription: '',
  paidFor: null,
  itemCount: null,
  orderValueDollars: null,
  recipientName: defaults?.recipientName || '',
  pickupInstructions: '',
  deliveryOptions: '',
  deliveryInstructions: '',
  driverTipDollars: null,
  notesForLP: '',
});

export const emptyDeliveryInfoFormResult = () => ({
  isValid: true,
  payload: null,
  notesForLP: '',
});

export type DeliveryValidationResult = {
  isValid: boolean;
  errors: string[];
};

export const emptyDeliveryValidationResult = (): DeliveryValidationResult => ({
  isValid: true,
  errors: [],
});

export const formatDeliveryValidationErrorMessage = (errors: string[]): string => {
  const lines = ['Please complete the delivery information before getting an estimate.'];

  for (const error of errors) {
    lines.push(`- ${error}`);
  }

  return lines.join('<br>');
};

export const isDeliveryServiceType = (serviceType: string): boolean =>
  serviceType === RideServiceType.PharmacyDelivery || serviceType === RideServiceType.SpecialRequest;
